"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "Do you have my tire size in stock right now?",
  answer: "Tire stock changes throughout the day as shipments arrive and jobs are completed. Call our team directly to verify current availability for your exact size."
}, {
  id: "item-2",
  question: "How do I find the correct tire size for my car?",
  answer: "Check the raised sequence of numbers on your tire sidewall (such as 225/50R17) or the tire placard sticker on the driver side door jamb. Your vehicle owner manual also lists recommended tire sizes."
}, {
  id: "item-3",
  question: "Do I need an appointment, or are walk-ins welcome?",
  answer: "Walk-ins are always welcome. You can pull right up to our shop during business hours for same-day flat repairs, tire replacements, or quick pressure checks."
}, {
  id: "item-4",
  question: "How do I know if a tire can be repaired or must be replaced?",
  answer: "Small punctures located along the center tread are typically safe to patch or plug. Cuts on the sidewall, shoulder punctures, or tires with worn tread need replacement. Call or visit us so our technicians can inspect the damage."
}, {
  id: "item-5",
  question: "How long does tire installation take?",
  answer: "Installation time depends on your vehicle, the number of tires being replaced, and how busy the shop is at that moment. Call ahead and our team can give you a better estimate for your visit."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faq" data-nav="light" className="bg-background text-foreground py-8 sm:py-11 md:py-14 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <HelpCircle className="size-3.5" />
            <span>Got questions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground">
            Quick answers about sizes, current availability, walk-ins, and tire repairs.
          </p>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card text-card-foreground rounded-lg border border-border p-4 sm:p-6 shadow-sm mb-6">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                <AccordionItem value={item.id} className="border-border py-1">
                  <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-foreground hover:no-underline py-3">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-accent/40 rounded-lg border border-border p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Have a specific tire size or repair question?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Speak directly with our team about tire size, availability, or same-day service.
            </p>
          </div>
          <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-sm shrink-0">
            <a href="tel:+14243138474">
              <Phone className="size-4 mr-2" />
              Call now
            </a>
          </Button>
        </motion.div>
      </div>
    </section>;
}